import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgentChat } from '@/context/AgentChatContext';
import { AgentMessageBubble, AgentProgressPanel, ToolCallIndicator } from './AgentMessage';
import { RenderCard } from './cards';

interface AgentChatShellProps {
  mode: 'pane' | 'page';
}

function AgentChatShell({ mode }: AgentChatShellProps) {
  const { t } = useTranslation();
  const {
    isOpen,
    toggleOpen,
    conversations,
    activeConversationId,
    setActiveConversationId,
    loadConversations,
    deleteConversation,
    startNewConversation,
    messages,
    isStreaming,
    activeToolCall,
    progressSteps,
    sendMessage,
    sendCardResponse,
    pendingCard,
    followUpSuggestions,
  } = useAgentChat();

  const [input, setInput] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isPane = mode === 'pane';
  const isPage = mode === 'page';
  const starterPromptsValue = t('agentChat.starterPrompts', {
    returnObjects: true,
    defaultValue: [],
  });

  const starterPrompts = Array.isArray(starterPromptsValue)
    ? starterPromptsValue.map(String)
    : [];

  const [historyCollapsed, setHistoryCollapsed] = useState(true);
  const shouldShowHistory = (isPage && !historyCollapsed) || showHistory;

  const sortedConversations = [...conversations].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );
  const visibleSuggestions =
    followUpSuggestions.length > 0
      ? followUpSuggestions
      : messages.length === 0 && !pendingCard
        ? starterPrompts
        : [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeToolCall, pendingCard]);

  useEffect(() => {
    if ((isPane && isOpen) || isPage) {
      loadConversations();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isPane, isPage, loadConversations]);

  const handleSend = useCallback(() => {
    if (!input.trim() || isStreaming) return;
    sendMessage(input.trim());
    setInput('');
  }, [input, isStreaming, sendMessage]);

  const handleCardResponse = useCallback(
    (cardId: string, cardType: string, selection: Record<string, unknown>) => {
      sendCardResponse(cardId, cardType, selection);
    },
    [sendCardResponse],
  );

  if (isPane && !isOpen) return null;

  const containerClassName = isPane
    ? 'fixed ltr:right-0 rtl:left-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl ltr:border-l rtl:border-r border-gray-200 z-50 flex flex-col animate-scale-in'
    : 'flex h-full min-h-0 w-full bg-white';

  const shellClassName = isPane
    ? 'flex h-full w-full flex-col'
    : 'flex h-full min-h-0 w-full overflow-hidden';

  const historyClassName = isPane
    ? 'border-b bg-gray-50 max-h-48 overflow-y-auto'
    : 'w-80 shrink-0 border-l border-blue-700/30 bg-gradient-to-b from-blue-800 to-blue-900 overflow-y-auto';

  const bodyClassName = isPane
    ? 'flex-1 overflow-y-auto px-3 py-4'
    : 'flex min-h-0 flex-1 flex-row';

  const messagesClassName = isPane
    ? ''
    : 'flex-1 overflow-y-auto px-6 py-6 lg:px-8';

  const inputWrapperClassName = isPane
    ? 'border-t bg-white px-3 py-3'
    : 'border-t bg-white px-6 py-4 lg:px-8';

  return (
    <div className={containerClassName}>
      <div className={shellClassName}>
        <div className="flex items-center justify-between border-b bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-3 text-white lg:px-6">
          <div className="flex items-center gap-2">
            <span className="text-lg">🫧</span>
            <h2 className="text-sm font-semibold">{t('agentChat.title', 'Dada Assistant')}</h2>
          </div>
          <div className="flex items-center gap-1">
            {isPane && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="rounded-lg p-1.5 text-xs transition-colors hover:bg-white/20"
                title={t('agentChat.history', 'History')}
              >
                📋
              </button>
            )}
            {isPane && (
              <button
                onClick={startNewConversation}
                className="rounded-lg p-1.5 text-xs transition-colors hover:bg-white/20"
                title={t('agentChat.newChat', 'New Chat')}
              >
                ➕
              </button>
            )}
            {isPane && (
              <button
                onClick={toggleOpen}
                className="rounded-lg p-1.5 transition-colors hover:bg-white/20"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className={bodyClassName}>
          {/* Pane mode: history on top */}
          {isPane && shouldShowHistory && (
            <div className={historyClassName}>
              {sortedConversations.length === 0 ? (
                <p className="p-3 text-center text-xs text-gray-400">
                  {t('agentChat.noConversations', 'No conversations yet')}
                </p>
              ) : (
                sortedConversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`flex cursor-pointer items-center justify-between border-b border-gray-100 px-3 py-2 hover:bg-gray-100 ${
                      activeConversationId === conv.id ? 'bg-primary-50' : ''
                    }`}
                  >
                    <button
                      onClick={() => {
                        setActiveConversationId(conv.id);
                        if (isPane) setShowHistory(false);
                      }}
                      className="flex-1 text-left"
                    >
                      <p className="truncate text-xs font-medium text-gray-700">
                        {conv.title || conv.last_message_preview || 'Untitled'}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {new Date(conv.updated_at).toLocaleDateString()}
                      </p>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(conv.id);
                      }}
                      className="p-1 text-xs text-gray-400 hover:text-red-500"
                    >
                      🗑
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Chat content: messages + input */}
          <div className={isPane ? '' : 'flex min-h-0 flex-1 flex-col'}>
            <div className={messagesClassName}>
              {messages.length === 0 && !isStreaming && (
                <div className={`flex h-full flex-col ${isPage ? 'justify-start' : 'justify-center'} text-gray-400`}>
                  <div className={`${isPage ? 'mx-auto mt-10 w-full max-w-3xl rounded-[28px] border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-900 p-6 text-white shadow-xl' : 'items-center text-center'}`}>
                    <span className={`mb-3 ${isPage ? 'text-3xl' : 'text-4xl'} block`}>🫧</span>
                    <p className={`${isPage ? 'text-2xl' : 'text-sm'} font-semibold`}>
                      {t('agentChat.welcome', 'Hi! How can I help?')}
                    </p>
                    <p className={`mt-2 ${isPage ? 'max-w-2xl text-sm text-slate-200' : 'max-w-[320px] text-xs'}`}>
                      {t(
                        'agentChat.welcomeHint',
                        'Ask me to create invoices, search customers, check orders, or anything else!',
                      )}
                    </p>
                  {isPage && (
                    <div className="mt-5 grid gap-3 sm:grid-cols-3">
                      {starterPrompts.map((prompt) => (
                        <button
                          key={prompt}
                          type="button"
                          onClick={() => setInput(prompt)}
                          className="rounded-2xl border border-slate-200 bg-slate-100/80 px-4 py-3 text-left text-sm text-slate-600 transition hover:bg-slate-200/80 hover:text-slate-700"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                    )}
                  </div>
                </div>
              )}

              {messages.map((msg, idx) => (
                <AgentMessageBubble key={idx} message={msg} />
              ))}

              {(isStreaming || pendingCard) && progressSteps.length > 0 && (
                <AgentProgressPanel steps={progressSteps} />
              )}

              {activeToolCall && <ToolCallIndicator toolName={activeToolCall} />}

              {pendingCard && (
                <RenderCard
                  cardType={pendingCard.card_type}
                  cardId={pendingCard.card_id}
                  data={pendingCard.data as Record<string, unknown>}
                  onResponse={handleCardResponse}
                />
              )}

              <div ref={messagesEndRef} />
            </div>

          <div className={inputWrapperClassName}>
            {visibleSuggestions.length > 0 && !isStreaming && !pendingCard && (
              <div className="mb-2 flex flex-wrap gap-2 animate-fade-in">
                {visibleSuggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(suggestion)}
                    className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder={t('agentChat.inputPlaceholder', 'Type a message...')}
                  disabled={isStreaming}
                  className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:opacity-50"
                  dir="auto"
                />
                <button
                  onClick={handleSend}
                  disabled={isStreaming || !input.trim()}
                  className="rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isStreaming ? (
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    '↗'
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Page mode: history sidebar on the right with blue theme */}
          {isPage && (
            <div
              className={`shrink-0 border-l border-blue-700/30 bg-gradient-to-b from-blue-800 to-blue-900 flex flex-col transition-all duration-300 ease-in-out ${
                shouldShowHistory ? 'w-80' : 'w-14'
              } overflow-hidden`}
            >
              {/* Sidebar action buttons */}
              <div className={`flex shrink-0 items-center gap-2 border-b border-blue-700/30 px-3 py-3 ${
                shouldShowHistory ? 'justify-between' : 'justify-center'
              }`}>
                <button
                  onClick={() => setHistoryCollapsed(!historyCollapsed)}
                  className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/20 ${
                    shouldShowHistory ? 'bg-white/10' : ''
                  }`}
                  title={t('agentChat.history', 'History')}
                >
                  📋
                  {shouldShowHistory && <span>{t('agentChat.history', 'History')}</span>}
                </button>
                {shouldShowHistory && (
                  <button
                    onClick={startNewConversation}
                    className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/20"
                    title={t('agentChat.newChat', 'New Chat')}
                  >
                    ➕ {t('agentChat.newChat', 'New Chat')}
                  </button>
                )}
              </div>

              {/* Conversation list */}
              {shouldShowHistory && (
                <div className="flex-1 overflow-y-auto">
                  {sortedConversations.length === 0 ? (
                    <p className="p-3 text-center text-xs text-blue-300">
                      {t('agentChat.noConversations', 'No conversations yet')}
                    </p>
                  ) : (
                    sortedConversations.map((conv) => (
                      <div
                        key={conv.id}
                        className={`flex cursor-pointer items-center justify-between border-b border-blue-700/30 px-3 py-2 hover:bg-blue-700/40 ${
                          activeConversationId === conv.id ? 'bg-blue-700/50' : ''
                        }`}
                      >
                        <button
                          onClick={() => setActiveConversationId(conv.id)}
                          className="flex-1 text-left"
                        >
                          <p className="truncate text-xs font-medium text-white">
                            {conv.title || conv.last_message_preview || 'Untitled'}
                          </p>
                          <p className="text-[10px] text-blue-300">
                            {new Date(conv.updated_at).toLocaleDateString()}
                          </p>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversation(conv.id);
                          }}
                          className="p-1 text-xs text-blue-300 hover:text-red-300"
                        >
                          🗑
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AgentChatPane() {
  return <AgentChatShell mode="pane" />;
}

export function AgentChatPage() {
  return <AgentChatShell mode="page" />;
}
